import aa from "search-insights";

type InsightsConfig = Pick<AlgoliaConfig, "algoliaAppId" | "algoliaSearchKey" | "userToken">;

type QueuedInsightsEvent = [method: string, payload: Record<string, unknown>];

interface InsightsQueue {
    push: (...events: QueuedInsightsEvent[]) => number;
}

declare global {
    interface Window {
        /**
         * Command queue for conversion events rendered by the server
         * (AlgoliaController::renderInsightsConversionEvent). The inline snippets run while the
         * page is parsed, before this deferred bundle has loaded, so they only push onto an array;
         * activateInsightsQueue() replays it once the insights client is initialised.
         */
        dcAlgoliaInsights?: QueuedInsightsEvent[] | InsightsQueue;
    }
}

const sendInsightsEvent = (method: string, payload: Record<string, unknown>): void => {
    (aa as unknown as (method: string, payload: Record<string, unknown>) => void)(method, payload);
};

// Must only run after aa("init"): search-insights rejects every call before that.
function activateInsightsQueue(): void {
    const queue = window.dcAlgoliaInsights;
    if (queue !== undefined && !Array.isArray(queue)) return;

    window.dcAlgoliaInsights = {
        push: (...events) => {
            events.forEach(([method, payload]) => sendInsightsEvent(method, payload));
            return events.length;
        },
    };

    queue?.forEach(([method, payload]) => sendInsightsEvent(method, payload));
}

export function initAlgoliaInsights(config: InsightsConfig) {
    if (typeof window === "undefined") return;

    aa("init", {
        appId: config.algoliaAppId,
        apiKey: config.algoliaSearchKey,
        useCookie: true,
    });

    if (config.userToken) {
        aa("setUserToken", config.userToken);
    }

    activateInsightsQueue();
}
